<template>
  <main>
    <SearchResultSection
      :result-count="doctors.length"
      :loading="availabilityLoading"
      @search="handleSearch"
    />

    <LayoutSection v-if="availabilityLoading && !doctors.length" class="space-y-8">
      <div
        v-for="n in 3"
        :key="n"
        class="bg-white-50 rounded-[2rem] p-4 sm:p-6 border border-[rgba(28,27,26,0.06)]"
      >
        <div class="md:flex gap-4">
          <div class="lg:w-1/2 space-y-4">
            <div class="flex items-center gap-3">
              <div class="w-[6.4rem] h-[6.4rem] rounded-full bg-black/[0.06] animate-pulse" />
              <div class="space-y-2">
                <div class="h-4 w-40 rounded bg-black/[0.06] animate-pulse" />
                <div class="h-3 w-24 rounded bg-black/[0.05] animate-pulse" />
              </div>
            </div>
            <div class="h-[3.4rem] w-1/2 rounded-full bg-black/[0.05] animate-pulse" />
            <div class="space-y-2">
              <div
                v-for="r in 4"
                :key="r"
                class="h-[4.4rem] rounded-[1.2rem] bg-black/[0.05] animate-pulse"
              />
            </div>
          </div>
          <div class="lg:w-1/2 mt-4 md:mt-0 space-y-3">
            <div class="h-5 w-40 mx-auto rounded bg-black/[0.06] animate-pulse" />
            <div class="grid grid-cols-4 gap-2">
              <div
                v-for="c in 16"
                :key="c"
                class="h-[3.6rem] rounded-lg bg-black/[0.05] animate-pulse"
              />
            </div>
          </div>
        </div>
      </div>
    </LayoutSection>

    <SpecialistsAvailableSection
      :doctors="doctors"
      :dates-loading="datesLoading"
      :base-date="currentWindowStart"
      :search-loading="availabilityLoading"
      :show-minimap="!filters.online"
      @request-more-dates="onRequestMoreDates"
      @request-calendar="onRequestCalendar"
      @search-area="onSearchArea"
    />

    <div
      v-if="hasMoreResults && doctors.length"
      ref="loadMoreSentinel"
      class="h-6"
    />

    <div
      v-if="isLoadingMore"
      class="flex items-center justify-center py-6 text-[1.2rem] text-gray-500"
    >
      <InlineSpinner size="24" />
    </div>

    <LayoutSection
      v-if="
        hasSearched && !availabilityLoading && !doctors.length && !isLoadingMore
      "
      :maxWidthClass="'w-full'"
      sectionClass="flex flex-col items-center justify-center min-h-[40vh] text-center"
    >
      <p class="text-[2rem] font-semibold text-gray-800">
        {{ $t("No results found") }}
      </p>
      <p class="mt-[1.2rem] text-[1.4rem] text-gray-500">
        {{ $t("Try adjusting your filters to discover other specialists.") }}
      </p>
    </LayoutSection>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useSeo } from "@/composables/useSeo";
import SearchResultSection from "@/components/search/searchResultSection.vue";
import SpecialistsAvailableSection from "@/components/search/specialistsAvailableSection.vue";
import LayoutSection from "@/components/design/layoutSection.vue";
import InlineSpinner from "@/components/loader/InlineSpinner.vue";
import { useSearch } from "@/composables";
import type { CalendarRequestPayload } from "@/types/domain";

const { t } = useI18n();
useSeo({ title: t("seoSearchTitle"), description: t("seoSearchDesc") });

const loadMoreSentinel = ref<HTMLElement | null>(null);

// Use the refactored composable
const {
  doctors,
  filters,
  availabilityLoading,
  currentWindowStart,
  hasSearched,
  datesLoading,
  hasMoreResults,
  isLoadingMore,
  handleRequestMoreDates,
  handleRequestCalendar,
  initializeLocation,
  search,
  loadMore,
  handleSearchArea,
} = useSearch({
  translateFn: t,
});

// Event handlers
async function handleSearch() {
  await search();
}

function onRequestMoreDates(doctor: any, payload: CalendarRequestPayload) {
  handleRequestMoreDates(doctor, payload);
}

function onRequestCalendar(doctor: any, payload: CalendarRequestPayload) {
  handleRequestCalendar(doctor, payload);
}

function onSearchArea(payload: {
  latitude: number | null;
  longitude: number | null;
  radiusKm?: number | null;
}) {
  handleSearchArea(payload);
}

// Intersection Observer for infinite scroll
let loadMoreObserver: IntersectionObserver | null = null;

function setupObserver() {
  if (loadMoreObserver) {
    loadMoreObserver.disconnect();
    loadMoreObserver = null;
  }

  if (!hasMoreResults.value || !loadMoreSentinel.value) return;
  if (typeof IntersectionObserver === "undefined") return;

  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting && !isLoadingMore.value) {
        loadMore();
      }
    },
    { rootMargin: "200px" },
  );

  loadMoreObserver.observe(loadMoreSentinel.value);
}

// Watch for changes that require observer update
watch([hasMoreResults, () => doctors.value.length], () => {
  setupObserver();
});

// Lifecycle hooks
onMounted(async () => {
  await initializeLocation();
  await search();
});

onBeforeUnmount(() => {
  if (loadMoreObserver) {
    loadMoreObserver.disconnect();
  }
});
</script>
